import { Tables } from "@/lib/supabase/supabase_types_override";
import { JsonValue, Where, WhereField } from "@/types/crm/configuration/question_conditionals";
import { airtableFieldTypesBaseTypes } from "@/constants/crm/airtable/fields";
import { salesforceFieldTypesBaseTypes } from "@/constants/crm/salesforce/fields";
import { collaborateFieldTypesBaseTypes } from "@/constants/crm/collaborate/fields";
import { QuestionWithConditionResult } from "@/features/question/hooks/useQuery/useGetQuestionsBySurveyId";

const LOG = false; // Set to true to enable detailed logging


export function evaluateQuestionConditionals(
    questionId: string,
    newValue: string,
    questions: Array<QuestionWithConditionResult>,
    userId: string = 'mock-user-id'
): Array<QuestionWithConditionResult> {
    // Create a simulated questions array with the new response
    const questionsWithSimulatedResponse = questions.map(question => {
        if (question.id === questionId) {
            // Create a mock response for this question
            const mockResponse: Tables<'response'> = {
                id: 'mock-response-id',
                user_id: userId,
                question_id: questionId,
                field_value: newValue,
                archived: false,
                created_at: new Date().toISOString(),
                valid: true,
                content: null
            };

            // Filter out any existing responses for this user and add the new one
            const filteredResponses = question.response.filter(r => r.user_id !== userId);

            return {
                ...question,
                response: [...filteredResponses, mockResponse]
            };
        }
        return question;
    });

    return evaluateQuestionsConditionals(
        questionsWithSimulatedResponse,
        [userId], // projectMembersIds
        userId    // userId
    )
}


export function evaluateQuestionsConditionals(
    questions: Array<QuestionWithConditionResult>,
    projectMembersIds: Tables<'user'>['id'][] = [],
    userId?: Tables<'user'>['id'],
): Array<QuestionWithConditionResult> {
    return questions.map(question => {
        if (!question.selected_configuration?.conditionals?.where ||
            question.selected_configuration.conditionals?.enabled === false
        ) {
            return {
                ...question,
                condition_met: true,
                condition_met_for_users: [],
            };
        }



        LOG && console.group("Condition Check for Question: ", question.question);

        const usersToCheck = projectMembersIds?.length > 0 ? projectMembersIds : [];
        const usersThatMetCondition: Tables<'user'>[] = [];
        console.log(usersToCheck)

        for (const userIdToCheck of usersToCheck) {
            const where = question.selected_configuration.conditionals.where;
            const result = evaluateWhere(where, questions, userIdToCheck);
            LOG && console.log(`Condition result for user ${userIdToCheck}:`, result.success);

            if (result.success) {
                if (result.user) {
                    usersThatMetCondition.push(result.user);
                }
            }
        }

        const conditionMet = usersThatMetCondition.find(user => user.id === userId) !== undefined;
        if (userId === undefined || userId === null) {
            return {
                ...question,
                condition_met: true,
                condition_met_for_users: usersThatMetCondition,
            };
        }
        LOG && console.log("Users that met condition:", usersThatMetCondition);
        LOG && console.log("Final condition result:", conditionMet);
        LOG && console.groupEnd();

        return {
            ...question,
            condition_met: conditionMet,
            // condition_met_for_users: usersThatMetCondition,
            condition_met_for_users: usersThatMetCondition,
        };
    });
}

function evaluateWhere(
    where: Where,
    questions: QuestionWithConditionResult[],
    userId: string,
): { success: boolean; user?: Tables<'user'> } {
    LOG && console.group("Evaluating Where condition:", where);

    let userInfo: Tables<'user'> | undefined;

    if (where.and && where.and.length > 0) {
        const andResults = where.and.map(condition =>
            evaluateWhere(condition, questions, userId)
        );
        const andResult = andResults.every(result => result.success);

        // Capture user info from the first successful result
        userInfo ??= andResults.find(result => result.user)?.user;

        LOG && console.log("AND result:", andResult);
        if (!andResult) {
            console.groupEnd();
            return { success: false, user: userInfo };
        }
    }

    if (where.or && where.or.length > 0) {
        const orResults = where.or.map(condition =>
            evaluateWhere(condition, questions, userId)
        );
        const orResult = orResults.some(result => result.success);

        // Capture user info from the first successful result
        userInfo ??= orResults.find(result => result.user)?.user;

        LOG && console.log("OR result:", orResult);
        if (!orResult) {
            console.groupEnd();
            return { success: false, user: userInfo };
        }
    }

    for (const [key, value] of Object.entries(where)) {
        if (key === 'and' || key === 'or') {
            continue; // Already handled above
        }

        // key should be a questionId
        const questionId = key;

        if (Array.isArray(value)) {
            // value is Where[], evaluate as OR of all conditions
            const arrayResults = value.map(condition =>
                evaluateWhere(condition, questions, userId)
            );
            const arrayResult = arrayResults.some(result => result.success);

            // Capture user info from the first successful result
            userInfo ??= arrayResults.find(result => result.user)?.user;

            LOG && console.log(`Question ${questionId} array result:`, arrayResult);
            if (!arrayResult) {
                console.groupEnd();
                return { success: false, user: userInfo };
            }
        } else {
            // value is WhereField, evaluate the field conditions
            const fieldResult = evaluateWhereField(
                questionId,
                value,
                questions,
                userId
            );

            // Capture user info from this result
            if (!userInfo && fieldResult.user) {
                userInfo = fieldResult.user;
            }

            LOG && console.log(`Question ${questionId} field result:`, fieldResult.success);
            if (!fieldResult.success) {
                console.groupEnd();
                return { success: false, user: userInfo };
            }
        }
    }
    LOG && console.groupEnd();
    return { success: true, user: userInfo };
}

function evaluateWhereField(
    questionId: string,
    whereField: WhereField,
    questions: QuestionWithConditionResult[],
    userId: string,
): { success: boolean; user?: Tables<'user'> } {
    const referencedQuestion = questions.find(q => q.id === questionId);
    if (!referencedQuestion) {
        LOG && console.warn(`Question with ID ${questionId} not found`);
        return { success: false };
    }

    const responses = referencedQuestion.response.filter(
        r => r.user_id === userId && !r.archived
    );
    LOG && console.log(referencedQuestion.question + ":" + responses?.find(r => r.valid) || responses?.at(-1))
    const evaluatedResponse = responses?.find(r => r.valid) ?? responses?.filter(r=>r.field_value !== null && r.field_value !== undefined && r.field_value !== "").at(-1);
    console.log(responses)
    console.log(evaluatedResponse)
    LOG && console.group(`Evaluating field condition for question: ${referencedQuestion.question}`);
    LOG && console.log("Last Response:", evaluatedResponse);
    LOG && console.log("Where Field:", whereField);

    if (!evaluatedResponse) {
        LOG && console.log("No response found for question:", referencedQuestion.question);
        LOG && console.groupEnd();
        return { success: false };
    }

    // Evaluate each operator in the WhereField
    for (const [operator, expectedValue] of Object.entries(whereField)) {
        if (expectedValue === undefined) continue;

        const conditionResult = evaluateFieldCondition(
            operator as keyof WhereField,
            expectedValue,
            evaluatedResponse.field_value,
            referencedQuestion
        );

        LOG && console.log(`Operator ${operator} with value ${expectedValue}: ${conditionResult}`);

        if (!conditionResult) {
            console.groupEnd();
            return { success: false };
        }
    }

    LOG && console.groupEnd();

    // Create user object with the userId being checked
    const user: Tables<'user'> = { id: userId } as Tables<'user'>;
    return { success: true, user };
}

export function evaluateFieldCondition(
    operator: keyof WhereField,
    expectedValue: JsonValue,
    actualValue: JsonValue,
    question: QuestionWithConditionResult,
): boolean {
    if (actualValue === undefined || actualValue === null) {
        return false;
    }

    const isNumericQuestion = inferQuestionType(question) === 'number';

    let conditionValue: unknown = expectedValue;
    let responseValue: unknown = actualValue;

    if (isNumericQuestion) {
        conditionValue = Number(expectedValue);
        responseValue = Number(actualValue);

        if (isNaN(conditionValue as number) || isNaN(responseValue as number)) {
            return false;
        }
    }

    switch (operator) {
        case 'equals':
            // Handle when expectedValue is an array (means "equals any of these values")
            if (Array.isArray(conditionValue)) {
                return conditionValue.includes(responseValue);
            }
            // Handle when responseValue is an array (means response contains multiple values)
            if (Array.isArray(responseValue)) {
                return responseValue.includes(conditionValue);
            }
            return responseValue === conditionValue;

        case 'not-equals':
            // Handle when expectedValue is an array
            if (Array.isArray(conditionValue)) {
                return !conditionValue.includes(responseValue);
            }
            // Handle when responseValue is an array
            if (Array.isArray(responseValue)) {
                return !responseValue.includes(conditionValue);
            }
            return responseValue !== conditionValue;

        case 'greater-than':
            if (isNumericQuestion) {
                return (responseValue as number) > (conditionValue as number);
            }
            return String(responseValue) > String(conditionValue);

        case 'less-than':
            if (isNumericQuestion) {
                return (responseValue as number) < (conditionValue as number);
            }
            return String(responseValue) < String(conditionValue);

        case 'greater-than-equals':
            if (isNumericQuestion) {
                return (responseValue as number) >= (conditionValue as number);
            }
            return String(responseValue) >= String(conditionValue);

        case 'less-than-equals':
            if (isNumericQuestion) {
                return (responseValue as number) <= (conditionValue as number);
            }
            return String(responseValue) <= String(conditionValue);

        case 'contains':
            return String(responseValue).includes(String(conditionValue));

        case 'reg-exp':
            try {
                let pattern = String(expectedValue);

                // Remove leading and trailing slashes if present (JavaScript regex literal format)
                if (pattern.startsWith('/') && pattern.includes('/', 1)) {
                    const lastSlashIndex = pattern.lastIndexOf('/');
                    const flags = pattern.substring(lastSlashIndex + 1);
                    pattern = pattern.substring(1, lastSlashIndex);

                    const regex = new RegExp(pattern, flags);
                    return regex.test(String(responseValue));
                } else {
                    // Pattern without slashes, use as-is
                    const regex = new RegExp(pattern);
                    return regex.test(String(responseValue));
                }
            } catch (e) {
                console.error("Invalid regex pattern:", expectedValue);
                return false;
            }

        default:
            LOG && console.warn(`Unknown operator: ${operator}`);
            return false;
    }
}

function inferQuestionType(question: QuestionWithConditionResult): 'number' | 'list' | 'text' | 'boolean' {
    if ('field_type' in question) {
        switch (question.crm_system) {
            case 'airtable':
                return airtableFieldTypesBaseTypes[question.field_type as keyof typeof airtableFieldTypesBaseTypes];
            case 'salesforce':
                return salesforceFieldTypesBaseTypes[question.field_type as keyof typeof salesforceFieldTypesBaseTypes];
            default:
                return collaborateFieldTypesBaseTypes[question.field_type as keyof typeof collaborateFieldTypesBaseTypes];
        }

    }
    return 'text';
}